/**
 * Shared action bar for AC/BC proposal detail pages — parity with the solar
 * Detail toolbar. Primary buttons (PDF, Email) + a "More" dropdown (Editar
 * dados, Recalcular, Duplicar, PDF Simplificado, Enviar para Odoo, Eliminar).
 *
 * Backed by the generic kind-agnostic action handlers (actions/duplicate,
 * actions/recalc, actions/delete, actions/update). PDF + Email are placeholder
 * buttons until the per-kind PDF template lands (the client provides the
 * model) — they surface an informative toast instead of failing silently.
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Building2, Copy, Edit as EditIcon, FileDown, Loader2,
  Mail, MoreHorizontal, RefreshCw, Trash2, Wrench,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { EmailDialog } from "@/components/EmailDialog"
import { editPathForKind, type Kind } from "@/lib/kind-labels"
import { proposalFileBase } from "@/lib/format"
import { downloadWithFilename, downloadFromBase64 } from "@/lib/download"
import { confirmDialog } from "@/lib/confirm"

export function ProposalActionBar({
  shell,
  kind,
  proposalId,
  number,
  clientName,
  clientEmail,
  odooOrderId,
  onOpenOdoo,
  onReload,
  editingKit,
  onToggleKit,
  locked = false,
}: {
  shell: AppShell | null
  kind: Kind
  proposalId: string
  number: string
  clientName: string
  clientEmail: string | null
  odooOrderId: number | string | null
  onOpenOdoo: () => void
  onReload: () => void
  /** Quando fornecido, mostra o toggle "Editar Kit" (edição inline das linhas). */
  editingKit?: boolean
  onToggleKit?: () => void
  /** Proposta ganha: sem editar, kit, recalcular nem eliminar. */
  locked?: boolean
}) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const editPath = editPathForKind(kind, proposalId)

  async function exportPdf() {
    if (!shell) return
    setBusy("pdf")
    try {
      const res = await shell.client.post<{
        success: boolean
        data?: {
          public_url: string | null
          pdf_base64?: string | null
          pdf_content_type?: string | null
        }
        error?: string
      }>("actions/export_pdf", { params: { id: proposalId } })
      if (!res.success || !res.data) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "PDF gerado" })
      const filename = `${proposalFileBase(number, clientName)}.pdf`
      // Preferir bytes base64 (download same-origin, nome garantido). Ver
      // src/lib/download.ts. Fallback: abrir o public_url cross-origin.
      if (res.data.pdf_base64) {
        downloadFromBase64(
          res.data.pdf_base64,
          filename,
          res.data.pdf_content_type ?? "application/pdf",
        )
        return
      }
      const url = res.data.public_url
      if (!url) return
      let absolute = url
      if (url.startsWith("/")) {
        const hostOrigin =
          (shell.context as { host_origin?: string }).host_origin ||
          (document.referrer && new URL(document.referrer).origin) ||
          ""
        absolute = `${hostOrigin}${url}`
      }
      // Nome amigável no fallback (r2-proxy usa ?filename= no Content-Disposition).
      absolute += `${absolute.includes("?") ? "&" : "?"}filename=${encodeURIComponent(filename)}`
      await downloadWithFilename(absolute, filename)
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a gerar PDF" })
    } finally {
      setBusy(null)
    }
  }


  async function recalc() {
    if (!shell) return
    setBusy("recalc")
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/recalc", { params: { id: proposalId } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Recalculado" })
      onReload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setBusy(null)
    }
  }

  async function duplicate() {
    if (!shell) return
    setBusy("duplicate")
    try {
      const res = await shell.client.post<{
        success: boolean; data?: { id: string; number: string }; error?: string
      }>("actions/duplicate", { params: { id: proposalId } })
      if (!res.success || !res.data) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: `Duplicado como ${res.data.number}` })
      navigate(`/ac/${res.data.id}`)
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    if (!shell) return
    if (!(await confirmDialog(`Eliminar orçamento ${number}?`, { destructive: true, confirmLabel: "Eliminar" }))) return
    setBusy("delete")
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/delete", { params: { id: proposalId } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Eliminado" })
      navigate("/list")
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {onToggleKit && !locked && (
        <Button variant={editingKit ? "default" : "outline"} onClick={onToggleKit}>
          <Wrench className="h-4 w-4" />
          {editingKit ? "Sair edição" : "Editar Kit"}
        </Button>
      )}
      <Button variant="outline" onClick={exportPdf} disabled={busy === "pdf"}>
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
      </Button>
      <Button variant="outline" onClick={() => setEmailOpen(true)}>
        <Mail className="h-4 w-4" /> Email
      </Button>
      {/* "Enviar para Odoo" fica só no dropdown (como no solar): a ordem de
          venda é criada ao converter a proposta em Ganho. */}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Mais opções">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          {editPath && !locked && (
            <DropdownMenuItem onSelect={() => navigate(editPath)}>
              <EditIcon className="h-4 w-4" /> Editar dados
            </DropdownMenuItem>
          )}
          {!locked && (
            <DropdownMenuItem onSelect={() => recalc()} disabled={busy === "recalc"}>
              <RefreshCw className={`h-4 w-4 ${busy === "recalc" ? "animate-spin" : ""}`} />
              Recalcular
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => duplicate()} disabled={busy === "duplicate"}>
            <Copy className="h-4 w-4" /> Duplicar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => exportPdf()} disabled={busy === "pdf"}>
            <FileDown className="h-4 w-4" /> Gerar PDF
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpenOdoo()}>
            <Building2 className="h-4 w-4" />
            {odooOrderId ? "Re-enviar para Odoo" : "Enviar para Odoo"}
          </DropdownMenuItem>
          {!locked && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => remove()}>
                <Trash2 className="h-4 w-4" /> Eliminar
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EmailDialog
        shell={shell}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        proposalId={proposalId}
        number={number}
        clientName={clientName}
        clientEmail={clientEmail}
        exportVerb="export_pdf"
        sendVerb="send_email"
      />
    </div>
  )
}
